// Contacts endpoints. The directory-typeahead (users.ts) is a global
// people search; the contacts endpoints are the user's own saved
// contact list with folders and favourites, server-mirrored so they
// sync across devices (migration 0029).
//
// BACKEND CONTRACT (services/api/internal/contacts):
//   GET    /v1/contacts                         → BundleResponse (account ids)
//   POST   /v1/contacts                         → add a contact
//        body: { account_id }
//   DELETE /v1/contacts/{account_id}            → remove a contact
//   POST   /v1/contacts/{account_id}/favourite  → set favourite state
//        body: { favourite }
//   POST   /v1/contacts/folders                 → create a folder
//        body: { name, contact_account_ids? }
//   DELETE /v1/contacts/folders/{folder_id}     → delete a folder
//
// The wire returns account ids only; we enrich them to display names +
// avatars via the batch profile endpoint (same as the search mappers).

import { del, get, NetworkError, post } from './api';
import type { DirectoryUser } from './users';
import { getUserProfiles, resolveMediaUrl } from './users';

export interface ContactFolder {
  readonly id: string;
  readonly name: string;
  readonly contactIds: readonly string[];
}

export interface ContactsBundle {
  readonly contacts: readonly DirectoryUser[];
  readonly favouriteIds: readonly string[];
  readonly folders: readonly ContactFolder[];
  readonly pendingRequestCount: number;
}

function isBackendMissing(err: unknown): boolean {
  return err instanceof NetworkError && (err.status === 404 || err.status === 501);
}

const EMPTY_BUNDLE: ContactsBundle = {
  contacts: [],
  favouriteIds: [],
  folders: [],
  pendingRequestCount: 0,
};

// A real 16-byte directory account id is base64url (22 chars, no padding).
// Device/manual contacts use synthetic ids (`contact:…`, `manual-…`) that the
// backend can't store — callers guard on this before hitting the server.
export function isServerContactId(id: string): boolean {
  return /^[A-Za-z0-9_-]{22}$/.test(id);
}

interface BundleWire {
  readonly contact_account_ids?: string[];
  readonly favourite_account_ids?: string[];
  readonly folders?: Array<{ id: string; name: string; contact_account_ids?: string[] }>;
  readonly pending_request_count?: number;
}

/** Enrich saved-contact account ids into `DirectoryUser`s (name + avatar). */
async function enrichContacts(
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<DirectoryUser[]> {
  if (ids.length === 0) return [];
  const profiles = await getUserProfiles(ids).catch(() => new Map());
  return Promise.all(
    ids.map(async (id): Promise<DirectoryUser> => {
      const p = profiles.get(id);
      const avatarUri = p?.avatarMediaId
        ? await resolveMediaUrl(p.avatarMediaId, signal)
        : undefined;
      return {
        id,
        username: id,
        displayName: p?.displayName ?? id,
        ...(avatarUri ? { avatarUri } : {}),
        ...(p?.jobTitle ? { role: p.jobTitle } : {}),
        ...(p?.companyName ? { company: p.companyName } : {}),
      };
    }),
  );
}

export async function loadContactsBundle(signal?: AbortSignal): Promise<ContactsBundle> {
  try {
    const opts = signal ? { signal } : {};
    const wire = await get<BundleWire>('/v1/contacts', opts);
    const contacts = await enrichContacts(wire.contact_account_ids ?? [], signal);
    return {
      contacts,
      favouriteIds: wire.favourite_account_ids ?? [],
      folders: (wire.folders ?? []).map((f) => ({
        id: f.id,
        name: f.name,
        contactIds: f.contact_account_ids ?? [],
      })),
      pendingRequestCount: wire.pending_request_count ?? 0,
    };
  } catch (err) {
    // Endpoint not deployed yet → empty bundle (list still shows conversation
    // peers + device contacts, which the host merges in).
    if (isBackendMissing(err)) return EMPTY_BUNDLE;
    throw err;
  }
}

export async function addContact(params: { userId: string }): Promise<void> {
  if (!isServerContactId(params.userId)) return; // device/manual contact — local only
  await post<void>('/v1/contacts', { account_id: params.userId });
}

export async function removeContact(params: { userId: string }): Promise<void> {
  if (!isServerContactId(params.userId)) return;
  await del<void>(`/v1/contacts/${encodeURIComponent(params.userId)}`);
}

export async function setFavourite(params: {
  userId: string;
  favourite: boolean;
}): Promise<void> {
  if (!isServerContactId(params.userId)) return;
  await post<void>(`/v1/contacts/${encodeURIComponent(params.userId)}/favourite`, {
    favourite: params.favourite,
  });
}

export async function createFolder(params: {
  name: string;
  contactIds?: readonly string[];
}): Promise<ContactFolder> {
  const serverIds = (params.contactIds ?? []).filter(isServerContactId);
  const wire = await post<{ id: string; name: string; contact_account_ids?: string[] }>(
    '/v1/contacts/folders',
    { name: params.name, contact_account_ids: serverIds },
  );
  return {
    id: wire.id,
    name: wire.name,
    contactIds: wire.contact_account_ids ?? [],
  };
}

export async function deleteFolder(params: { folderId: string }): Promise<void> {
  await del<void>(`/v1/contacts/folders/${encodeURIComponent(params.folderId)}`);
}
